// The console's *Thresholds* area (CR-066: "TA Admin configuration without code: … SLA/health thresholds …"; G §15
// "SLA/health thresholds"; B10 plan D14, slice S20b; acceptance A-38, A-11). Registered into S20a's area registry
// (register.ts) for the kinds `sla_thresholds`, `health_thresholds` and `phases`; S20a's console draws the versioned
// table and calls this editor with one key. The editor is typed per kind and edits a copy of the version in effect; its
// *Validate* opens S20a's Journey (Change → Validate → Version → Activate → Audit: `config.validate`, then
// `config.propose`, a second Admin approving), so this file writes nothing itself. Locked values are shown, never
// editable: `scores_people` ("Health informs attention; it does not make employment decisions.").
//
// This file also holds the plumbing the three S20b editors share: the version in effect (`useEffectiveBody`), the
// draft, the immutable path update (`withPath`) and the frame with *Validate* (`EditorFrame`).

import { useState, type ReactNode } from "react";
import {
  Banner, Button, Card, EmptyState, Field, G6_LABELS, HEALTH_BAND_LABELS, Input, JOURNEY_LABELS, LEVEL_DISPLAY, METRIC_LABELS, RULE_LABELS,
  SLA_LABELS, SLA_TIER_LABELS, Stack, SURFACE_LABELS, Table, Td, Th, Tr, VERSION_TEMPLATES, type Hs, type Level,
} from "@/mc";
import { configList, configOpen } from "@/api/tools";
import type { AppError } from "@/api/runtime";
import { errorText, useRead } from "@/api/useTool";
import { Journey as ConfigJourney } from "@/views/b10/settings/Journey";
import { CONSOLE_AREA_LABELS } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

// ---- shared plumbing ------------------------------------------------------------------------------------------------

export type Body = Record<string, unknown>;
type Path = readonly (string | number)[];

/** A copy of `body` with `value` at `path`, every object and array on the path copied, nothing else touched. */
export function withPath<T>(body: T, path: Path, value: unknown): T {
  if (path.length === 0) return value as T;
  const [head, ...rest] = path;
  if (Array.isArray(body)) {
    const copy = [...body];
    copy[head as number] = withPath(copy[head as number], rest, value);
    return copy as T;
  }
  const source = (typeof body === "object" && body !== null ? body : {}) as Record<string, unknown>;
  return { ...source, [head as string]: withPath(source[head as string], rest, value) } as T;
}

/** The value at `path`, or undefined. */
export function atPath(body: unknown, path: Path): unknown {
  let at: unknown = body;
  for (const part of path) {
    if (at === null || typeof at !== "object") return undefined;
    at = (at as Record<string | number, unknown>)[part];
  }
  return at;
}

/** A number typed into a field, or null when the text is not one. */
export function numberOf(text: string): number | null {
  const trimmed = text.trim();
  if (trimmed === "" || !/^-?\d+(\.\d+)?$/.test(trimmed)) return null;
  return Number(trimmed);
}

/** The version in effect of one key: its body and number (`config.list` effective, then `config.open`). */
export function useEffectiveBody(configKey: string) {
  return useRead(["cfg", "effective", configKey], async () => {
    const list = await configList({ key: configKey, status: "effective", limit: 1 });
    const row = list.rows[0];
    if (!row) return { body: null as Body | null, version: null as number | null };
    const open = await configOpen({ id: row.id });
    return { body: (open.payload ?? {}) as Body, version: open.version as number | null };
  });
}

/** The draft over the version in effect: the edited copy once something changed, else the version itself. */
export function useDraft(base: Body | null | undefined) {
  const [draft, setDraft] = useState<Body | null>(null);
  const current = draft ?? base ?? null;
  const dirty = draft !== null && JSON.stringify(draft) !== JSON.stringify(base ?? null);
  const set = (path: Path, value: unknown) => setDraft((d) => withPath(d ?? base ?? {}, path, value));
  return { current, dirty, set, reset: () => setDraft(null) };
}

/** The Journey closing: the slide-over always closes; only a version it proposed resets the edits and re-reads the key.
 * Closed without proposing (`null`), the edits stay, as they do after *Edit* (`onBack`). */
export function closeJourney(proposal: unknown, close: () => void, proposed?: (() => void) | undefined): void {
  close();
  if (proposal) proposed?.();
}

export interface EditorFrameProps {
  kind: string;
  configKey: string;
  title: string;
  version: number | null | undefined;
  loading?: boolean | undefined;
  error?: AppError | null | undefined;
  onRetry?: (() => void) | undefined;
  /** The edited body handed to the Journey. */
  draft: Body | null;
  /** Read-only: no Validate (a locked or read-only key). */
  readOnly?: boolean | undefined;
  /** A version was proposed: reset the draft and re-read the key. */
  onDone?: (() => void) | undefined;
  children?: ReactNode;
}

/** The editor's card: the key in mono and its version, the typed fields, and *Validate*, which opens S20a's Journey with
 * the edited body. *Edit* in the Journey comes back here with the edits kept. */
export function EditorFrame({ kind, configKey, title, version, loading, error, onRetry, draft, readOnly, onDone, children }: EditorFrameProps) {
  const [journey, setJourney] = useState(false);
  if (error) return <Card title={title} error={{ message: errorText(error), onRetry, details: { correlationId: error.requestId ?? undefined, status: error.status } }} />;
  return (
    <Card
      title={title}
      sub={(
        <>
          <span className="mc-mono">{configKey}</span>
          {version !== null && version !== undefined ? <> · <span className="mc-mono">{VERSION_TEMPLATES.version({ n: version })}</span></> : null}
        </>
      )}
      loading={loading ? { lines: 5 } : undefined}
      actions={readOnly || !draft ? undefined : (
        <Button sm icon="check" onClick={() => setJourney(true)} hs={hsKey("cfg", kind, "validate")}>{JOURNEY_LABELS.validate}</Button>
      )}
    >
      {children}
      {journey && draft ? (
        <ConfigJourney
          kind={kind}
          configKey={configKey}
          body={draft}
          onBack={() => setJourney(false)}
          onDone={(proposal) => closeJourney(proposal, () => setJourney(false), onDone)}
        />
      ) : null}
    </Card>
  );
}

/** A small number field in mono; its words are the field's label. */
export function NumberField({ label, value, onChange, hs, min, max, step }: {
  label: ReactNode;
  value: number | null | undefined;
  onChange: (value: number) => void;
  hs?: Hs | undefined;
  min?: number | undefined;
  max?: number | undefined;
  step?: number | undefined;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <Field label={label}>
      <Input
        sm
        mono
        inputMode="decimal"
        type="number"
        min={min}
        max={max}
        step={step}
        value={text ?? (value === null || value === undefined ? "" : String(value))}
        onChange={(event) => {
          setText(event.target.value);
          const n = numberOf(event.target.value);
          if (n !== null) onChange(n);
        }}
        onBlur={() => setText(null)}
        hs={hs}
      />
    </Field>
  );
}

// ---- sla_thresholds ---------------------------------------------------------------------------------------------------

/** A fraction of a clock's active target as the whole percent the field shows (0.5 → 50). */
export const toPercent = (fraction: unknown): number | null => (typeof fraction === "number" ? Math.round(fraction * 1000) / 10 : null);
/** The percent typed back as the stored fraction (50 → 0.5). */
export const fromPercent = (percent: number): number => Math.round(percent * 10) / 1000;

/** The per-clock overrides of `sla_thresholds.clocks`, in key order. */
export function clockOverrides(body: Body | null): { clock: string; half: number | null; nearBreach: number | null }[] {
  const clocks = (atPath(body, ["clocks"]) ?? {}) as Record<string, { half?: number; near_breach?: number }>;
  return Object.keys(clocks)
    .sort()
    .map((clock) => ({ clock, half: toPercent(clocks[clock]?.half), nearBreach: toPercent(clocks[clock]?.near_breach) }));
}

export function SlaThresholdsForm({ body, set }: { body: Body | null; set: (path: Path, value: unknown) => void }) {
  const rows = clockOverrides(body);
  return (
    <Stack>
      <Banner tone="info" icon="clock">{RULE_LABELS.slaUsesActiveTime}</Banner>
      <Stack gap={10}>
        <NumberField label={SLA_TIER_LABELS.reminder} value={toPercent(atPath(body, ["tiers", "half"]))} min={1} max={99} onChange={(n) => set(["tiers", "half"], fromPercent(n))}  />
        <NumberField label={SLA_TIER_LABELS.nearBreach} value={toPercent(atPath(body, ["tiers", "near_breach"]))} min={1} max={99} onChange={(n) => set(["tiers", "near_breach"], fromPercent(n))}  />
      </Stack>
      {rows.length > 0 ? (
        <Table
          dense
          label={G6_LABELS.thresholds}
          head={<Tr><Th>{CONSOLE_AREA_LABELS.sla}</Th><Th align="r">{SLA_TIER_LABELS.reminder}</Th><Th align="r">{SLA_TIER_LABELS.nearBreach}</Th></Tr>}
        >
          {rows.map((row) => (
            <Tr key={row.clock}>
              <Td mono label={CONSOLE_AREA_LABELS.sla}>{row.clock}</Td>
              <Td align="r" label={SLA_TIER_LABELS.reminder}>
                <NumberField label={<span className="mc-mono">{row.clock}</span>} value={row.half} min={1} max={99} onChange={(n) => set(["clocks", row.clock, "half"], fromPercent(n))} hs={hsKey("cfg.sla", row.clock, "half")} />
              </Td>
              <Td align="r" label={SLA_TIER_LABELS.nearBreach}>
                <NumberField label={<span className="mc-mono">{row.clock}</span>} value={row.nearBreach} min={1} max={99} onChange={(n) => set(["clocks", row.clock, "near_breach"], fromPercent(n))} hs={hsKey("cfg.sla", row.clock, "near-breach")} />
              </Td>
            </Tr>
          ))}
        </Table>
      ) : null}
    </Stack>
  );
}

// ---- health_thresholds ------------------------------------------------------------------------------------------------

/** His words for the drivers (D-24: *Days open*, *HM responsiveness*, *Overdue review* lead); a driver without one keeps
 * its key, in mono. */
export const DRIVER_WORD: Readonly<Record<string, string>> = {
  days_open: METRIC_LABELS.daysOpen,
  hm_responsiveness: METRIC_LABELS.hmResponsiveness,
  overdue_actions: METRIC_LABELS.overdueReview,
  sla_breaches: SLA_TIER_LABELS.breach,
  time_to_acceptance: METRIC_LABELS.timeToAcceptance,
  hold_days: SLA_LABELS.daysOnHold,
};

/** The driver weights as rows (driver, then each weight's key and value), in the body's order. */
export function driverRows(body: Body | null): { driver: string; weights: { key: string; value: number }[] }[] {
  const drivers = (atPath(body, ["drivers"]) ?? {}) as Record<string, Record<string, number>>;
  return Object.entries(drivers).map(([driver, weights]) => ({
    driver,
    weights: Object.entries(weights ?? {}).map(([key, value]) => ({ key, value })),
  }));
}

export function HealthThresholdsForm({ body, set }: { body: Body | null; set: (path: Path, value: unknown) => void }) {
  return (
    <Stack>
      <Banner tone="info" icon="lock">{RULE_LABELS.healthInformsAttention}</Banner>
      <Stack gap={10}>
        <NumberField label={METRIC_LABELS.reqHealth} value={atPath(body, ["start"]) as number | undefined} min={0} max={100} onChange={(n) => set(["start"], Math.round(n))}  />
        <NumberField label={HEALTH_BAND_LABELS.green} value={atPath(body, ["light", "green_min"]) as number | undefined} min={0} max={100} onChange={(n) => set(["light", "green_min"], Math.round(n))}  />
        <NumberField label={HEALTH_BAND_LABELS.yellow} value={atPath(body, ["light", "yellow_min"]) as number | undefined} min={0} max={100} onChange={(n) => set(["light", "yellow_min"], Math.round(n))}  />
      </Stack>
      <Table dense label={SURFACE_LABELS.drivers} head={<Tr><Th>{SURFACE_LABELS.drivers}</Th><Th>{G6_LABELS.thresholds}</Th></Tr>}>
        {driverRows(body).map((row) => (
          <Tr key={row.driver}>
            <Td label={SURFACE_LABELS.drivers}>{DRIVER_WORD[row.driver] ?? <span className="mc-mono">{row.driver}</span>}</Td>
            <Td label={G6_LABELS.thresholds}>
              <Stack gap={6}>
                {row.weights.map((w) => (
                  <NumberField
                    key={w.key}
                    label={<span className="mc-mono">{w.key}</span>}
                    value={w.value}
                    max={0}
                    onChange={(n) => set(["drivers", row.driver, w.key], Math.round(n))}
                    hs={hsKey("cfg.health", row.driver, w.key)}
                  />
                ))}
              </Stack>
            </Td>
          </Tr>
        ))}
      </Table>
    </Stack>
  );
}

// ---- phases -----------------------------------------------------------------------------------------------------------

export const LEVELS: readonly Level[] = ["manager_and_below", "director_srdirector", "vp_and_above"];

/** The phase-target grid: one row per phase key (the union over levels, in the first level's order), a day count per
 * level. */
export function phaseGrid(body: Body | null): { phase: string; days: Record<Level, number | null> }[] {
  const targets = (atPath(body, ["phase_targets"]) ?? {}) as Record<string, Record<string, number>>;
  const keys: string[] = [];
  for (const level of LEVELS) for (const key of Object.keys(targets[level] ?? {})) if (!keys.includes(key)) keys.push(key);
  return keys.map((phase) => ({
    phase,
    days: Object.fromEntries(LEVELS.map((level) => [level, targets[level]?.[phase] ?? null])) as Record<Level, number | null>,
  }));
}

export function PhasesForm({ body, set }: { body: Body | null; set: (path: Path, value: unknown) => void }) {
  return (
    <Table
      dense
      label={G6_LABELS.thresholds}
      head={<Tr><Th>{G6_LABELS.thresholds}</Th>{LEVELS.map((level) => <Th key={level} align="r">{LEVEL_DISPLAY[level]}</Th>)}</Tr>}
    >
      {phaseGrid(body).map((row) => (
        <Tr key={row.phase}>
          <Td label={G6_LABELS.thresholds}>{row.phase === "total" ? METRIC_LABELS.timeToAcceptance : <span className="mc-mono">{row.phase}</span>}</Td>
          {LEVELS.map((level) => (
            <Td key={level} align="r" label={LEVEL_DISPLAY[level]}>
              <NumberField
                label={<span className="mc-mono">{LEVEL_DISPLAY[level]} · {METRIC_LABELS.days}</span>}
                value={row.days[level]}
                min={0}
                onChange={(n) => set(["phase_targets", level, row.phase], Math.round(n))}
                hs={hsKey("cfg.phases", level, row.phase)}
              />
            </Td>
          ))}
        </Tr>
      ))}
    </Table>
  );
}

// ---- the editor -------------------------------------------------------------------------------------------------------

/** The area's editor for one key of `sla_thresholds`, `health_thresholds` or `phases` (S20a's Editor contract). */
export function ThresholdsEditor({ kind, configKey }: { kind: string; configKey: string }) {
  const q = useEffectiveBody(configKey);
  const draft = useDraft(q.data?.body);
  const form = kind === "sla_thresholds" ? <SlaThresholdsForm body={draft.current} set={draft.set} />
    : kind === "health_thresholds" ? <HealthThresholdsForm body={draft.current} set={draft.set} />
    : kind === "phases" ? <PhasesForm body={draft.current} set={draft.set} />
    : <EmptyState inline>—</EmptyState>;
  return (
    <EditorFrame
      kind={kind}
      configKey={configKey}
      title={G6_LABELS.thresholds}
      version={q.data?.version}
      loading={q.isLoading}
      error={q.error}
      onRetry={() => void q.refetch()}
      draft={draft.current}
      onDone={() => { draft.reset(); void q.refetch(); }}
    >
      {form}
    </EditorFrame>
  );
}
